import React, { useState } from 'react';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { penaltyAPI } from '@/lib/api';
import { toast } from 'sonner';
import { Loader2, Banknote, Gift } from 'lucide-react';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const fmt = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n || 0);

export default function PenaltyDeductDialog({ penalty, mode = 'salary', onOpenChange, onDeducted }) {
  const isIncentive = mode === 'incentive';
  const now = new Date();
  const [month, setMonth] = useState(String(now.getMonth() + 1));
  const [year, setYear] = useState(String(now.getFullYear()));
  const [saving, setSaving] = useState(false);

  const years = [now.getFullYear() - 1, now.getFullYear(), now.getFullYear() + 1];

  const handleDeduct = async () => {
    setSaving(true);
    try {
      const res = await penaltyAPI.deduct(penalty.id, parseInt(month), parseInt(year), mode);
      if (isIncentive) {
        toast.success(`Marked as deducted from incentive (${MONTHS[parseInt(month) - 1]} ${year})`);
      } else {
        const sr = res.data.salary_record;
        toast.success(`Deducted ${fmt(penalty.amount)} — new bank transfer: ${fmt(sr.bank_transfer)}`);
      }
      onDeducted();
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to deduct penalty');
    }
    setSaving(false);
  };

  return (
    <Dialog open={!!penalty} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-sm" data-testid="penalty-deduct-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isIncentive ? <Gift className="h-5 w-5 text-indigo-600" /> : <Banknote className="h-5 w-5 text-emerald-600" />}
            {isIncentive ? 'Deduct from Incentive' : 'Deduct via Salary Recovery'}
          </DialogTitle>
          <DialogDescription>
            {isIncentive
              ? `${penalty?.employee_name} — ${fmt(penalty?.amount)} will be recorded as deducted from the incentive of the selected month. Salary entry numbers are NOT changed; adjust the incentive payout outside the app.`
              : `${penalty?.employee_name} — ${fmt(penalty?.amount)} will be added to the Recovery Amount of the selected month's salary entry (reason: Penalty).`}
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-3 py-2">
          <div>
            <Label className="text-xs">{isIncentive ? 'Incentive Month' : 'Salary Month'}</Label>
            <Select value={month} onValueChange={setMonth}>
              <SelectTrigger className="mt-1" data-testid="deduct-month-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {MONTHS.map((m, i) => <SelectItem key={m} value={String(i + 1)}>{m}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Year</Label>
            <Select value={year} onValueChange={setYear}>
              <SelectTrigger className="mt-1" data-testid="deduct-year-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {years.map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            className={isIncentive ? 'bg-indigo-600 hover:bg-indigo-700' : 'bg-emerald-600 hover:bg-emerald-700'}
            onClick={handleDeduct} disabled={saving} data-testid="deduct-confirm-btn">
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Deduct {fmt(penalty?.amount)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
